import { writable } from 'svelte/store';
import collect from 'collect.js';
import config from '@/config';
import MultiGroup from '@/Models/MultiGroup';
import { mergeOrAddModelDataInCollectionStore } from '@/Stores/StoreHelpers';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	const { subscribe, set, update } = writable(collect([]));

	const methods = {
		getMultiGroupsForSport(sportId) {
			if (!config.SPORT_ENABLED) return;

			const multiGroup = new MultiGroup();
			let results = multiGroup.getBySportId(sportId);
			return results
				.then((result) => {
					set(collect(result.data));
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getMultiGroup(multiGroupId) {
			if (!config.SPORT_ENABLED) return;

			const multiGroup = new MultiGroup();

			return multiGroup
				.with([])
				.find(multiGroupId)
				.then((result) => {
					return result;
				});
		},

		newMultiGroup(sport) {
			// Look for an existing "new" group that never got persisted and wipe whatever data it has
			let foundExisting = false;
			update((multiGroups) => {
				multiGroups = multiGroups.transform((multiGroup) => {
					if (multiGroup.id == 'create') {
						multiGroup.name = '';
						multiGroup.sport = sport;
						multiGroup.excluded_market_types = { data: collect([]) };
						foundExisting = true;
					}
					return multiGroup;
				});
				return multiGroups;
			});
			// Assuming there isn't one already existing create a new one
			if (!foundExisting) {
				let multiGroup = new MultiGroup();
				multiGroup = multiGroup.hydrate({
					id: 'create',
					name: '',
					sport: sport
				});
				multiGroup.excluded_market_types = { data: collect([]) };
				mergeOrAddModelDataInCollectionStore(multiGroup, this);
			}
		},

		updateMultiGroupName(newName, multiGroupId) {
			update((multiGroups) => {
				multiGroups = multiGroups.transform((multiGroup) => {
					if (multiGroup.id == multiGroupId) {
						multiGroup.name = newName;
					}
					return multiGroup;
				});
				return multiGroups;
			});
		},

		addMarketTypeToGroup(marketType, multiGroupId) {
			update((multiGroups) => {
				multiGroups = multiGroups.transform((multiGroup) => {
					if (multiGroup.id == multiGroupId) {
						multiGroup.excluded_market_types.data.push(marketType);
					}
					return multiGroup;
				});
				return multiGroups;
			});
		},

		removeMarketTypeFromGroup(marketType, multiGroupId) {
			update((multiGroups) => {
				multiGroups = multiGroups.transform((multiGroup) => {
					if (multiGroup.id == multiGroupId) {
						multiGroup.excluded_market_types.data = multiGroup.excluded_market_types.data.reject((market_type) => {
							return market_type.id == marketType.id;
						});
					}
					return multiGroup;
				});
				return multiGroups;
			});
		},

		resetMultiGroup(multiGroupId) {
			update((multiGroups) => {
				multiGroups = multiGroups.transform((multiGroup) => {
					if (multiGroup.id == multiGroupId) {
						multiGroup.excluded_market_types = { data: collect([]) };
					}
					return multiGroup;
				});
				return multiGroups;
			});
		},

		createMultiGroup(sportId, multiGroupData) {
			const multiGroup = new MultiGroup();

			return multiGroup
				.makePersistRequest({
					url: multiGroup.resourceUrl(),
					method: 'PUT',
					data: multiGroupData
				})
				.then((result) => {
					this.getMultiGroupsForSport(sportId);
					return result;
				});
		},
		updateMultiGroup(multiGroup, overrideFormData = {}) {
			return multiGroup
				.makePersistRequest({
					url: multiGroup.selfUrl(),
					method: 'PATCH',
					data: multiGroup.preparePayload(overrideFormData)
				})
				.then((result) => {
					this.getMultiGroupsForSport(multiGroup.sport.id);
					return result;
				});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
